import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchKpiMyPerformance } from "../redux/slice/kpi-widget-metrics";
import { getRBDPerformanceMetrics } from "../redux/slice/rbd-performance-metrics";
import { getLeadershipMetrics } from "../redux/slice/leadership-metrics";

/**
 * Same "goals_data_disclaimer_show" flag used on the My Performance
 * dashboard widgets (StartTracking, RegionTracking, RegionalPerformance,
 * TerritoryPerformance, MyPerformanceSummary — see
 * useMyPerformanceController.js). Reused here so the chat page can show the
 * same "Goal values shown are from the previous quarter..." disclaimer
 * without depending on the chat API to send its own copy of this flag.
 *
 * Fetches the underlying per-role metrics itself (each guarded to run once)
 * so this works even if the person opens Chat without having visited My
 * Performance first in this session. If that page has already fetched the
 * data, these dispatches are skipped and the existing Redux data is reused.
 */
export function useGoalsDataDisclaimer() {
  const dispatch = useDispatch();

  const authUser = useSelector((state) => state.auth.user);
  const role = (authUser?.business_function || "").trim().toUpperCase();

  const isRBD = role === "RBD";
  const isLeadership = role === "LEADERSHIP";
  const isSalesRep = role === "SALESREP";

  const kpiMyPerformance = useSelector(
    (state) => state.kpiWidgetMetrics.myPerformance,
  );
  const rbdMetrics = useSelector(
    (state) => state.rbdPerformanceMetrics.rbdMetrics,
  );
  const leadershipMetrics = useSelector(
    (state) => state.leadershipMetrics.metrics,
  );

  const ranRbdFetch = useRef(false);
  const ranSalesRepFetch = useRef(false);
  const ranLeadershipFetch = useRef(false);

  useEffect(() => {
    if (!isRBD || ranRbdFetch.current) return;
    // Already fetched (e.g. person visited My Performance already this
    // session) — reuse it instead of re-fetching. `rbdMetrics` defaults to
    // `{}` (not null), so check for actual keys rather than truthiness.
    if (rbdMetrics && Object.keys(rbdMetrics).length > 0) {
      ranRbdFetch.current = true;
      return;
    }
    ranRbdFetch.current = true;
    dispatch(getRBDPerformanceMetrics());
  }, [dispatch, isRBD, rbdMetrics]);

  useEffect(() => {
    if (!isSalesRep || ranSalesRepFetch.current) return;
    if (kpiMyPerformance?.data) {
      ranSalesRepFetch.current = true;
      return;
    }
    ranSalesRepFetch.current = true;
    dispatch(fetchKpiMyPerformance());
  }, [dispatch, isSalesRep, kpiMyPerformance?.data]);

  useEffect(() => {
    if (!isLeadership || ranLeadershipFetch.current) return;
    if (leadershipMetrics && Object.keys(leadershipMetrics).length > 0) {
      ranLeadershipFetch.current = true;
      return;
    }
    ranLeadershipFetch.current = true;
    dispatch(getLeadershipMetrics());
  }, [dispatch, isLeadership, leadershipMetrics]);

  const goalsDataDisclaimerShow = useMemo(() => {
    if (isSalesRep) {
      return !!kpiMyPerformance?.data?.goals_data_disclaimer_show;
    }
    if (isRBD) {
      return !!rbdMetrics?.goals_data_disclaimer_show;
    }
    if (isLeadership) {
      return !!leadershipMetrics?.goals_data_disclaimer_show;
    }
    return false;
  }, [
    isSalesRep,
    isRBD,
    isLeadership,
    kpiMyPerformance?.data,
    rbdMetrics,
    leadershipMetrics,
  ]);

  return { goalsDataDisclaimerShow, isSalesRep, role };
}